import { CHART_PALETTE } from "@/config/theme";

const COST_COLORS = CHART_PALETTE;

/**
 * Convert a numeric cost value into human-friendly representation.
 * - Values < 1,000 are returned as-is with up to 1 decimal place
 * - Values >= 1,000 are represented in 'K' (thousands) with 1 decimal
 * - null returns "0"
 * - Non-finite numbers (NaN, Infinity) return their String(value)
 * @param {number | null} value
 * @returns {string}
 */
export const ConversionToK = (value: number | null): string => {
  if (value === null) return "0";
  if (Number.isNaN(value) || !Number.isFinite(value)) return String(value);
  const absValue = Math.abs(value);
  if (absValue < 1000) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return (value / 1000).toFixed(1) + "K";
};

/**
 * Format number into locale string with one decimal digit (en-US).
 * Returns "0" for undefined/null.
 * @param {number | undefined} value
 * @returns {string}
 */
export const ConvertToLocale = (value: number | undefined): string => {
  if (value === undefined || value === null) return "0";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

/**
 * Convert an array of cost items for table rendering.
 * - Assigns a color from theme palette
 * - Adds isK flag when value >= 1000
 * - Sorts items by descending value
 */
export const ConvertCostArrayForTable = <
  T extends { name: string; value: number },
>(
  data: T[],
) => {
  return data
    .map((item, index) => ({
      ...item,
      color: COST_COLORS[index % COST_COLORS.length],
      isK: item.value >= 1000,
    }))
    .sort((a, b) => b.value - a.value);
};

/**
 * Prepare data for donut chart consumption.
 * - Sorts by value descending
 * - If more than 10 items, groups remaining into an "Others" slice
 * - Assigns colors from theme palette
 */
export const ConvertCostArrayForDonut = <
  T extends { name: string; value: number },
>(
  data: T[],
) => {
  if (!data || data.length === 0) return [];

  const sorted = [...data].sort((a, b) => b.value - a.value);

  if (sorted.length <= 10) {
    return sorted.map((item, index) => ({
      ...item,
      color: COST_COLORS[index % COST_COLORS.length],
    }));
  }

  const top9 = sorted.slice(0, 9);
  const remaining = sorted.slice(9);
  const othersTotal = remaining.reduce((sum, i) => sum + i.value, 0);

  const convertedTop9 = top9.map((item, index) => ({
    ...item,
    color: COST_COLORS[index % COST_COLORS.length],
  }));

  const others = {
    name: "Others",
    value: othersTotal,
    color: COST_COLORS[9],
  };

  return [...convertedTop9, others];
};

/**
 * Calculate total cost value from an array and return formatted string with one decimal.
 * @template T
 * @param {T[]} data
 * @returns {string}
 */
export const calculateTotalCostValue = <T extends { value: number }>(
  data: T[],
): string => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);

  return total.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};
